/**
 * All numeral locale variants bundled together.
 * The tenant config.json specifies which locale to use via "numeralLocale".
 *
 * Variants differ in: thousands/decimal separators, precision, currency symbol.
 */

const baseAbbreviations = {
  thousand: 'k',
  million: 'M',
  billion: 'B',
  trillion: 'T',
};

const baseOrdinal = (number) => (number === 1 ? 'er' : 'º');

const locales = {
  // Default: comma thousands, dot decimal, 2 precision, RD$ symbol
  // Used by: wapa, billet, bn, firstdata, macro, mio, newpartners, openpay-ar,
  //          santander, tacataca, uala-mx, xpay
  'es': {
    delimiters: { thousands: ',', decimal: '.', precision: 2 },
    abbreviations: baseAbbreviations,
    ordinal: baseOrdinal,
    currency: { symbol: 'RD$' },
  },

  // MXN: comma thousands, dot decimal, 2 precision, $ symbol
  // Used by: bajio, banco-de-chile, credomatic, punto-clave
  'es-mx': {
    delimiters: { thousands: ',', decimal: '.', precision: 2 },
    abbreviations: baseAbbreviations,
    ordinal: baseOrdinal,
    currency: { symbol: '$' },
  },

  // USD: comma thousands, dot decimal, 2 precision, U$D symbol
  // Used by: bendo, payscan
  'es-usd': {
    delimiters: { thousands: ',', decimal: '.', precision: 2 },
    abbreviations: baseAbbreviations,
    ordinal: baseOrdinal,
    currency: { symbol: 'U$D' },
  },

  // CLP: comma thousands, dot decimal, 0 precision, $ symbol
  // Used by: compraqui
  'es-clp': {
    delimiters: { thousands: ',', decimal: '.', precision: 0 },
    abbreviations: baseAbbreviations,
    ordinal: baseOrdinal,
    currency: { symbol: '$' },
  },

  // AR: dot thousands, comma decimal, 2 precision, $ symbol
  // Used by: cabal
  'es-ar': {
    delimiters: { thousands: '.', decimal: ',', precision: 2 },
    abbreviations: baseAbbreviations,
    ordinal: baseOrdinal,
    currency: { symbol: '$' },
  },

  // PYG: dot thousands, comma decimal, 0 precision, Gs. symbol
  // Used by: itau-py
  'es-pyg': {
    delimiters: { thousands: '.', decimal: ',', precision: 0 },
    abbreviations: baseAbbreviations,
    ordinal: baseOrdinal,
    currency: { symbol: 'Gs.' },
  },

  // COP: comma thousands, dot decimal, 2 precision, COP symbol
  // Used by: lulopay
  'es-cop': {
    delimiters: { thousands: ',', decimal: '.', precision: 2 },
    abbreviations: baseAbbreviations,
    ordinal: baseOrdinal,
    currency: { symbol: 'COP' },
  },

  // PEN: comma thousands, dot decimal, 2 precision, S/ symbol
  // Used by: vendemas
  'es-pen': {
    delimiters: { thousands: ',', decimal: '.', precision: 2 },
    abbreviations: baseAbbreviations,
    ordinal: baseOrdinal,
    currency: { symbol: 'S/' },
  },
};

export default locales;

/**
 * Get a numeral locale by name, with fallback to 'es'.
 * @param {string} localeName - The locale name from tenant config.json
 * @returns {object} The numeral locale definition
 */
export const getNumeralLocale = (localeName) => {
  return locales[localeName] || locales['es'];
};
